import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { BackIcon } from "../../components/Icon";
import Input from "../../components/Input";
import Dropdown from "../../components/Dropdown";
import CareModal from "./CareModal";
import LoadingSpinner from "../../components/spinners/DrinkSpinner";
import { createPayment, getOrganization, getOrgTables } from "../../utility";
import createTableOrder from "../../utility/createTableOrder";
import ENV from "../../env";
import { setEmail, emptyWholeCart } from "../../redux/cart/reducer";
import { emailValidation } from "../../helper/regexValidations";
import { hasEnoughStockForCart } from "../../utility/stockManagement";

const CashlessPayment = () => {
  const { t } = useTranslation("fastpass");
  const [careModalOpen, setCareModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const savedEmail = useSelector((state) => state.cartReducer.email);
  const [email, setEmailValue] = useState(savedEmail || "");
  const [org, setOrg] = useState();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [note, setNote] = useState("");
  const alert = useAlert();

  const navigate = useNavigate();
  const { orgId } = useParams();
  const cartStore = useSelector((state) => state.cartReducer.cartByOrg[orgId] || []);
  const dispatch = useDispatch();

  let total = 0;
  let fee = 0;
  const toNumber = (value) => {
    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  cartStore?.map((i) => {
    total = parseFloat(i?.totalDue + total);
  });
  fee = parseFloat(total * 0.028);

  const tableOptions = useMemo(
    () =>
      tables.map((table) => ({
        value: table.id,
        label: table.customName
          ? `${table.number}. ${table.customName}`
          : `${t("table")} ${table.number}`,
      })),
    [tables, t],
  );

  const fetchOrgAndTables = useCallback(
    async (signal) => {
      setInitialLoading(true);
      try {
        const orgData = await getOrganization(orgId);
        if (signal.aborted) return;

        setOrg(orgData.org);
        if (orgData.org?.isTablePlanActive) {
          const fetchedTables = await getOrgTables(orgId);
          if (!signal.aborted) setTables([...fetchedTables].sort((a, b) => a.number - b.number));
        } else {
          setTables([]);
        }
      } catch (err) {
        console.error("Error loading organization:", err);
      } finally {
        if (!signal.aborted) setInitialLoading(false);
      }
    },
    [orgId],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchOrgAndTables(controller.signal);
    return () => controller.abort();
  }, [fetchOrgAndTables]);

  const handleEmailChange = (e) => {
    setEmailValue(e.target.value);
  };

  const handlePayment = async () => {
    if (!emailValidation(email)) {
      alert.show(t("invalidEmail"), { type: "error" });
    } else if (org?.isTablePlanActive && !selectedTable) {
      alert.show(t("pleaseSelectTable"), { type: "error" });
    } else if (cartStore.length === 0) {
      alert.show(t("pleaseSelectAtLeastOneItem"), { type: "error" });
    } else if (!hasEnoughStockForCart(cartStore)) {
      alert.show(t("maxQuantityReached"), { type: "error" });
    } else {
      setLoading(true);
      const products = cartStore.map((product) => ({
        ...product,
        id: product.id.split(":")[0],
        price: toNumber(product.price),
        quantity: toNumber(product.quantity),
        totalDue: toNumber(product.totalDue),
      }));

      try {
        if (org?.isTablePlanActive) {
          const selectedTableData = tables.find((table) => table.id === selectedTable);

          await createTableOrder({
            amount: total,
            products,
            orgId,
            note,
            tableId: selectedTable,
            tableNumber: selectedTableData?.number,
            creatorName: email,
            tableName: selectedTableData?.customName || "",
          });

          dispatch(emptyWholeCart({ orgId }));
          alert.show(t("tableOrderAdded"), { type: "success" });
          navigate(`/cashless/${orgId}`);
        } else {
          dispatch(setEmail(email));

          await createPayment({
            amount: total,
            products,
            customerEmail: email,
            orgId,
            checkoutUrl: `${ENV.hostname}/cashless/${orgId}`,
            note,
          });

          alert.show(t("redirecting"), { type: "success" });
        }
      } catch (err) {
        alert.show(org?.isTablePlanActive ? t("tableOrderAddFailed") : t("paymentFailed"), {
          type: "error",
        });
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <div className="main-page">
        <div className="container px-4 d-flex flex-column" style={{ minHeight: "100dvh" }}>
          {initialLoading ? (
            <div className="d-flex align-items-center justify-content-center flex-grow-1">
              <LoadingSpinner height="100%" width="100%" />
            </div>
          ) : (
            <>
              <div className="page-title">
                <button
                  type="button"
                  className="back-icon"
                  onClick={() => navigate(`/cashless/${orgId}`)}
                >
                  <BackIcon />
                </button>
                {org?.isTablePlanActive ? t("checkoutTableNote") : t("payment")}
              </div>
              <div className="price--area mb-30">
                <h2 className="price">{total.toFixed(2)}€</h2>
                <div className="subtext">{t("totalDue")}</div>
                {!org?.isTablePlanActive && (
                  <div className="text">
                    {t("quickPayFees")} {fee.toFixed(2)}€
                  </div>
                )}
              </div>

              <div className="text-22 gilroy-bold text-white mb-15">{t("emailForReceipt")}</div>
              <Input
                placeholder={t("emailPlaceholder")}
                label="email"
                style={{ height: "60px", paddingInline: "20px", marginBottom: "10px" }}
                onChange={handleEmailChange}
                value={email}
              />

              {org?.isTablePlanActive && (
                <div className="mt-30 mb-15">
                  <label className="form-label __form-label">{t("chooseTable")}</label>
                  <Dropdown
                    options={tableOptions}
                    onChange={(o) => setSelectedTable(o.value)}
                    value={selectedTable}
                    placeholder={t("chooseTable")}
                  />
                </div>
              )}

              <div className="mt-40 mb-30">
                <Input
                  placeholder=""
                  label={t("note")}
                  onChange={(e) => setNote(e.target.value)}
                  value={note}
                />
              </div>

              <div className="mt-auto text-center">
                <button
                  type="button"
                  className="submitBtn submitBtn2 m-0 mb-1"
                  onClick={handlePayment}
                >
                  {loading ? t("loading") : org?.isTablePlanActive ? t("addToTable") : t("pay")}
                </button>

                <div className="mt-12 pb-4">
                  {org?.isTablePlanActive ? t("tableOrderHelp") : t("paySecurelyOnline")}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <CareModal open={careModalOpen} setOpen={setCareModalOpen} />
    </>
  );
};

export default CashlessPayment;
